// checklistTextGetOptions
import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * @returns Returns a list of available checklist items which can be added to a distributionboard or finalcircuit, also includes all remark/recommendation texts and what options should be available.
 */
export function useAvailableChecklists({
    electricalProjectVersionId,
    kind,
}: {
    electricalProjectVersionId: string;
    kind: components['schemas']['ElectricalCheckListItemKind'];
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!kind) {
        throw new Error('objectId is required');
    }

    const checklists = useSuspenseQuery(elektraAppQueryOptions.project.checklistTextGetOptions({
        electricalProjectVersionId,
    }));

    const filteredChecklists = useMemo(() => {
        return checklists.data?.Items?.filter(
            checklist => checklist.Kind === kind,
        ) || [];
    }, [checklists.data, kind]);

    return {
        data: {
            items: filteredChecklists as components['schemas']['ListElectricalCheckListTextForProjectQueryResultItem'][],
            count: filteredChecklists.length,
        },
        isLoading: checklists.isLoading,
        isError: checklists.isError,
        error: checklists.error,
    };
}

export type MergedChecklistItems = components['schemas']['ListElectricalProjectCheckListItemQueryResultItem'] & components['schemas']['ListElectricalCheckListTextForProjectQueryResultItem'];

export function useChecklists({
    electricalProjectVersionId,
    checklistKind,
    objectId,
    objectKind,
}: {
    electricalProjectVersionId: string;
    checklistKind: components['schemas']['ElectricalProjectCheckListItemObjectKind'];
    objectId: string;
    objectKind: components['schemas']['ElectricalCheckListItemKind'];
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!checklistKind) {
        throw new Error('checklistKind is required');
    }

    if (!objectId) {
        throw new Error('objectId is required');
    }

    const checklists = useSuspenseQuery(elektraAppQueryOptions.project.checkListItemsOptions({
        electricalProjectVersionId,
    }));

    const checklistTexts = useSuspenseQuery(elektraAppQueryOptions.project.checklistTextGetOptions({
        electricalProjectVersionId,
    }));

    const checkListItemsMerged: MergedChecklistItems[] = useMemo(() => {
        return checklists.data?.Items?.map((item) => {
            const checklistText = checklistTexts.data?.Items?.find(text =>
                text.ElectricalCheckListItemId === item.ElectricalCheckListItemId,
            );
            return {
                ...item,
                ...checklistText,
                ElectricalProjectDistributionBoardId: item.ElectricalProjectDistributionBoardId,
                ElectricalProjectFinalCircuitId: item.ElectricalProjectFinalCircuitId,
                ObjectKind: item.ObjectKind,
            } as MergedChecklistItems;
        }) || [];
    }, [checklists.data, checklistTexts.data]);

    const filteredChecklists = useMemo(() => {
        return checkListItemsMerged.filter(
            checklist => checklist.ObjectKind === checklistKind && checklist.Kind === objectKind && (
                checklist.ElectricalProjectDistributionBoardId === objectId
                || checklist.ElectricalProjectFinalCircuitId === objectId
                || electricalProjectVersionId === objectId
            ),
        );
    }, [checkListItemsMerged, checklistKind, objectKind, objectId, electricalProjectVersionId]);

    return {
        data: {
            items: filteredChecklists as MergedChecklistItems[],
            count: filteredChecklists.length,
        },
        isLoading: checklists.isLoading || checklistTexts.isLoading,
        isError: checklists.isError || checklistTexts.isError,
        error: checklists.error || checklistTexts.error,
    };
}

export function useChecklistItem({
    electricalProjectVersionId,
    electricalCheckListItemId,
}: {
    electricalProjectVersionId: string;
    electricalCheckListItemId: string;
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!electricalCheckListItemId) {
        throw new Error('electricalCheckListItemId is required');
    }

    const checklistItem = useSuspenseQuery(elektraAppQueryOptions.project.checklistTextGetOptions({
        electricalProjectVersionId,
    }));

    const checklistItemData = useMemo(() => {
        return checklistItem.data?.Items?.find(item => item.ElectricalCheckListItemId === electricalCheckListItemId);
    }, [checklistItem.data, electricalCheckListItemId]);

    return {
        data: checklistItemData as components['schemas']['ListElectricalCheckListTextForProjectQueryResultItem'],
        isLoading: checklistItem.isLoading,
        isError: checklistItem.isError,
        error: checklistItem.error,
    };
}
